import { LitElement, html, css, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import '@awesome.me/webawesome/dist/components/dropdown/dropdown.js';
import '@awesome.me/webawesome/dist/components/popover/popover.js';
import '@awesome.me/webawesome/dist/components/tooltip/tooltip.js';
import '@awesome.me/webawesome/dist/components/divider/divider.js';
import { setStylesView, toggleNotesCollapse, toggleStructureView, toggleStylesView } from "./shared";
import { OutputFormat, outputFormatsLabels } from "../pandoc";
import WaSwitch from "@awesome.me/webawesome/dist/components/switch/switch.js";
import WaDropdownItem from "@awesome.me/webawesome/dist/components/dropdown-item/dropdown-item";

@customElement("od-header")
export class OutDesignHeader extends LitElement {
    static styles = css`
    :host {
        width: 100%;
        display: flex;
        border-bottom: solid 3px lightgray;
        caret-color: transparent;
    }
    .left, .right {
        flex-direction: columns;
        align-items: center;
        display: flex;
    }
    .left {
        flex-grow: 1;
        gap:0.4em;
        padding-left: 0.25em;
    }
    .left p {
        margin: 0;
        font-weight: bold;
        margin: 0 auto;
    }
    .left #buttons {
        display: flex;
    }
    #buttons #export wa-button::part(base) {
        border-left: none;
        border-right: none;
    }
    .right {
        height: 100%;
        align-items: center;
        right: 0;
        gap: 1em;
    }
    #title p {
        font-size: 1.25em;
    }
    #logo {
        display: flex;
        font-weight: bold;
        font-size: 1.1em;
        background: rgb(100, 15, 50);
        color: rgb(253, 69, 170);
        border-radius: 0.4em;
        height: 1.8em;
        width: 1.8em;
        text-align: center;
        filter: invert(1);
        align-items: center;
        transition: filter .15s;
    }
    #logo:hover {
        filter: none;
    }
    #emojis {
        margin: 0 0.3em 0 0;
        cursor: help;
        font-size: 1.1em;
        text-wrap: nowrap;
    }
    .left wa-button::part(base) {
        border-radius: 0;
        border-top: none;
        border-bottom: none;
        border-color: lightgray;
        border-width: 2px;
        background: white;
        transition: background .15s;
    }
    .left wa-button::part(base):hover {
        background: lightgray;
    }
    wa-dropdown {
        margin-right: 0.25em;
        overflow: scroll;
    }
    wa-dropdown h3 {
        display: block !important;
        margin: 0.25em 0px !important;
        padding: 0.25em 0.75em !important;
        color: var(--wa-color-text-quiet);
        font-family: var(--wa-font-family-body) !important;
        font-weight: var(--wa-font-weight-semibold) !important;
        font-size: var(--wa-font-size-smaller) !important;
    }
    input {
        display: none;
    }
    wa-tooltip a {
        color: orange;
    }
    wa-switch::part(label) {
        font-weight: bold;
        font-variant: all-small-caps;
        color: grey;
        margin: auto inherit;
    }
    wa-switch:not([disabled]):hover::part(label) {
        color: black;
    }
    wa-switch:state(checked)::part(control) {
        background-color: #8cafbf;
        border-color: #8cafbf;
    }
    wa-popover {
        text-align: center;
    }
    wa-popover::part(popup) {
        margin:auto;
    }
    wa-popover::part(body) {
        max-height: 90vh;
        top: 5vh;
        position: fixed;
        width: 50%;
        left: 25% ;
        box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
    }
    .more-exports {
        overflow: scroll;
        display: grid;
        grid-template-columns: repeat(2, auto);
        grid-gap: 1px;
    }
    wa-popover h2 {
        margin: 0;
    }
    #dismiss {
        margin: 0 auto;
    }
    `

    connectedCallback() {
        super.connectedCallback();
        document.addEventListener("enable-indesign-styles", this.enableInDesignStyles);
        document.addEventListener("disable-indesign-styles", this.disableInDesignStyles);
    }
    
    disconnectedCallback() {
        document.removeEventListener("enable-indesign-styles", this.enableInDesignStyles);
        document.removeEventListener("disable-indesign-styles", this.disableInDesignStyles);
        super.disconnectedCallback();
    }

    private renderExportFormats(popupId: string, action: (event: Event) => void, submenu: boolean = false) {
        return html`
            ${this.mainOutputFormats.map((format: OutputFormat) => {
                return html`
                    <wa-dropdown-item slot=${submenu ? "submenu" : nothing } value="${format}" @click=${action}>
                        ${outputFormatsLabels[format].label} (.${outputFormatsLabels[format].ext})
                    </wa-dropdown-item>
                `
                })
            }
            <wa-dropdown-item slot=${submenu ? "submenu" : nothing } id=${popupId}><b>Other formats...</b></wa-dropdown-item>
        `
    }

    private renderPopupExportFormats(popupId: string, action: (event: Event) => void) {
        return html`
            <wa-popover for=${popupId} without-arrow style="--max-width: 90vw;">
                <h2>All export formats</h2>
                <wa-divider></wa-divider>
                <div class="more-exports">
                    ${(Object.keys(outputFormatsLabels) as Array<OutputFormat>)
                        // .filter(key => !this.mainOutputFormats.includes(key as OutputFormat)))
                        .map((format: OutputFormat) => {
                        return html`
                            <wa-button appearance="plain" value="${format}" @click=${action}>
                                <b>${outputFormatsLabels[format].label}</b> (.${outputFormatsLabels[format].ext})
                            </wa-button>
                        `
                        })
                    }
                </div>
                <wa-divider></wa-divider>
                <div id="dismiss">
                    <wa-button appearance="filled" data-popover="close" variant="primary">Dismiss</wa-button>
                </div>
            </wa-popover>
        `
    }

    protected render() {
        const exportDocPopupId = "popover__doc__opening"
        const exportConverterPopupId = "popover__converter__opening"

        return html`
        <div class="left">
            <div id="logo"><p>Od</p></div>
            <div id="title"><p>OutDesign</p></div>
            <div id="buttons">
                <wa-dropdown id="open" class="dropdown-submenus">
                    <wa-button appearance="filled" slot="trigger" size="small">Open</wa-button>
                    <wa-dropdown-item value="idml">
                        <label for="idml-upload">IDML document</label>
                        <input id="idml-upload"
                        type="file" accept=".idml"
                        @change=${this.handleIdmlFileSelected}/>
                    </wa-dropdown-item>
                    <wa-dropdown-item value="json">
                        <label for="json-upload">JSON rules</label>
                        <input id="json-upload"
                        type="file" accept=".json"
                        @change=${this.handleJsonFileSelected}/>
                    </wa-dropdown-item>
                    <wa-dropdown-item>
                        Load an example
                        <wa-dropdown-item slot="submenu" @click=${this.loadSample} value="hello_world">Hello world (.idml)</wa-dropdown-item>
                        <wa-dropdown-item slot="submenu" @click=${this.loadSample} value="bollo">Déborder Bolloré (.dbk)</wa-dropdown-item>
                        <wa-dropdown-item slot="submenu" @click=${this.loadSample} value="indesign_test_suite">InDesign test suite (.dbk)</wa-dropdown-item>
                        <wa-dropdown-item slot="submenu" @click=${this.loadSample} value="pandoc_test_suite">Pandoc test suite (.md)</wa-dropdown-item>
                    </wa-dropdown-item>
                </wa-dropdown>
                <wa-dropdown id="export">
                    <wa-button appearance="filled" slot="trigger" size="small">Export</wa-button>
                    <h3>Converter</h3>
                    <wa-dropdown-item>
                        Export converter (.zip)
                        <h3 slot="submenu">Target format</h3>
                        ${this.renderExportFormats(exportDocPopupId, this.exportConverter, true)}
                    </wa-dropdown-item>
                    <h3>Rules</h3>
                    <wa-dropdown-item @click=${this.exportRules}>Export rules (.json)</wa-dropdown-item>
                    <h3>Document (Pandoc)</h3>
                    ${this.renderExportFormats(exportConverterPopupId, this.exportDoc)}
                    <h3>Styles</h3>
                    <wa-dropdown-item id="export-styles" @click=${this.exportStyles}>Export InDesign styles (.css)</wa-dropdown-item>
                </wa-dropdown>
                <wa-dropdown id="tools">
                    <wa-button appearance="filled" slot="trigger" size="small">Tools</wa-button>
                    <wa-dropdown-item @click=${this.resetWorkspace}>Reset workspace</wa-dropdown-item>
                    <wa-dropdown-item @click=${this.deleteAst}>Delete current document</wa-dropdown-item>
                    <wa-dropdown-item @click=${this.deleteRules}>Delete all rules</wa-dropdown-item>
                    <wa-dropdown-item value="markdown-cp" disabled>Document in clipboard (Markdown)</wa-dropdown-item>
                    <wa-dropdown-item value="json-cp" disabled>Rules in clipboard (JSON)</wa-dropdown-item>
                </wa-dropdown>
            </div>
        </div>
        <div class="right">
            <wa-switch id="structure-view" size="small" @change=${this.toggleStructureView} checked>View Structure</wa-switch>
            <wa-switch id="collapse-notes" size="small" @change=${this.toggleNotesCollapse} checked>Collapse notes</wa-switch>
            <wa-switch id="indesign-styles-switch" size="small" @change=${this.toggleStylesView} checked>InDesign Styles</wa-switch>
            <wa-tooltip for="emojis" placement="bottom-left">
                This program was developed in the context of the <i>Déborder Bolloré</i> project,
                where it was necessary to bring together the expert skills of graphic designers
                using Adobe InDesign and web developers, with the goal of producing a
                multiformat publication accessible in any reading context.
                This interface is presented very succinctly on Mastodon (in
                <a href="https://mastodon.design/@yann_t/116560894601961442">French</a> and in
                <a href="https://mastodon.design/@yann_t/116561002939311309">English</a>).
                All the code source is available on the
                <a href="https://codeberg.org/outdesign">Codeberg organisation</a>.
            </wa-tooltip>
            <p id="emojis">🏃‍♀️‍➡️➡️🚪</p>
        </div>
        ${this.renderPopupExportFormats(exportDocPopupId, this.exportConverter)}
        ${this.renderPopupExportFormats(exportConverterPopupId, this.exportDoc)}
        `;
    }

    private mainOutputFormats: Array<OutputFormat> = [
        "asciidoc",
        "docbook",
        "docx",
        "html",
        // "json",
        "latex",
        "markdown",
        "odt",
    ]

    private handleIdmlFileSelected(event: Event): void {
        this.dispatchEvent(
            new CustomEvent("idml-loaded", {
                detail: event.target,
                bubbles: true,
                composed: true
            })
        );
    }

    private handleJsonFileSelected(event: Event): void {
        this.dispatchEvent(
            new CustomEvent("json-loaded", {
                detail: event.target,
                bubbles: true,
                composed: true
            })
        );
    }

    private loadSample(event: Event): void {
        const sample = (event.target as HTMLInputElement).value
        this.dispatchEvent(
            new CustomEvent("load-sample", {
                detail: sample,
                bubbles: true,
                composed: true
            })
        );
    }

    private exportConverter(event: Event): void {
        const to: OutputFormat = (event.target as HTMLInputElement).value as OutputFormat
        this.dispatchEvent(
            new CustomEvent("export-converter", {
                detail: to,
                bubbles: true,
                composed: true
            })
        );
    }

    private exportRules(): void {
        this.dispatchEvent(
            new CustomEvent("export-rules", {
                bubbles: true,
                composed: true
            })
        );
    }

    private exportDoc(event: Event): void {
        const to: OutputFormat = (event.target as HTMLInputElement).value as OutputFormat
        this.dispatchEvent(
            new CustomEvent("export-doc", {
                detail: {
                    to: to,
                    ext: outputFormatsLabels[to].ext
                },
                bubbles: true,
                composed: true
            })
        );
    }

    private exportStyles(): void {
        this.dispatchEvent(
            new CustomEvent("export-styles", {
                bubbles: true,
                composed: true
            })
        );
    }

    private resetWorkspace(): void {
        this.deleteAst();
        this.deleteRules();
    }

    private deleteAst(): void {
        this.dispatchEvent(
            new CustomEvent("delete-ast", {
                bubbles: true,
                composed: true
            })
        )
    }

    private deleteRules(): void {
        this.dispatchEvent(
            new CustomEvent("delete-rules", {
                bubbles: true,
                composed: true
            })
        )
    }

    private toggleStylesView(): void {
        toggleStylesView()
        this.dispatchEvent(
            new CustomEvent("toggle-styles-view", {
                bubbles: true,
                composed: true
            })
        )
    }

    private toggleStructureView(): void {
        toggleStructureView()
        this.dispatchEvent(
            new CustomEvent("toggle-structure-view", {
                bubbles: true,
                composed: true
            })
        )
    }

    private toggleNotesCollapse(): void {
        toggleNotesCollapse()
        this.dispatchEvent(
            new CustomEvent("toggle-notes-collapse", {
                bubbles: true,
                composed: true
            })
        )
    }

    private enableInDesignStyles = () => {
        const switchElem = this.renderRoot.querySelector("#indesign-styles-switch") as WaSwitch
        if(switchElem) {
            switchElem.disabled = false
            switchElem.checked = true
        }

        const buttonElem = this.renderRoot.querySelector("#export-styles") as WaDropdownItem
        if(buttonElem) {
            buttonElem.disabled = false
        }

        setStylesView(true)
    }

    private disableInDesignStyles = () => {
        const switchElem = this.renderRoot.querySelector("#indesign-styles-switch") as WaSwitch
        if(switchElem) {
            switchElem.disabled = true
            switchElem.checked = false
        }

        const buttonElem = this.renderRoot.querySelector("#export-styles") as WaDropdownItem
        if(buttonElem) {
            buttonElem.disabled = true
        }

        setStylesView(false)
    }
}